/**
 * オンボーディング 3ステップ (Phase 6).
 *
 * 新規登録後のユーザーを最短で First Value に導く。
 * Step 1: サイト情報入力 (名前 + URL)
 * Step 2: 埋め込みコード設置 + 接続確認
 * Step 3: 最初の記事を作成
 *
 * Figma: #15-17 (OB 3ステップ), #70 (設置確認失敗)
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { type FormEvent, useEffect, useRef, useState } from 'react';
import { createSite, verifyWidget } from '../lib/api-client.js';
import { formatErrorMessage } from '../lib/error-messages.js';
import { trackEvent } from '../lib/posthog.js';
import { buildWidgetLoaderScript, fetchWidgetLoaderInfo } from '../lib/widget-manifest.js';

type Step = 1 | 2 | 3;

export function OnboardingPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [siteName, setSiteName] = useState('');
  const [originHost, setOriginHost] = useState('');
  const [siteId, setSiteId] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const [connected, setConnected] = useState(false);
  const [copied, setCopied] = useState(false);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    };
  }, []);

  useEffect(() => {
    document.title = 'はじめに - PetaCMS';
  }, []);

  const { data: widgetLoader } = useQuery({
    queryKey: ['widget-loader'],
    queryFn: () => fetchWidgetLoaderInfo(),
    staleTime: 300_000,
  });

  // Step 1: サイト作成
  const createMutation = useMutation({
    mutationFn: () => createSite({ name: siteName, originHost }),
    onSuccess: (result) => {
      setSiteId(result.id);
      trackEvent('onboarding_site_created', { siteId: result.id });
      setStep(2);
    },
    onError: (err) => {
      setError(formatErrorMessage(err, 'サイトの作成に失敗しました'));
    },
  });

  const handleStep1 = (e: FormEvent) => {
    e.preventDefault();
    setError('');
    createMutation.mutate();
  };

  // Step 2: 接続確認
  const embedCode = buildWidgetLoaderScript(siteId, widgetLoader);

  const markCopied = () => {
    setCopied(true);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopied(false), 2000);
  };

  const handleCopyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      markCopied();
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = embedCode;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      markCopied();
    }
  };

  const handleCheckConnection = async () => {
    setChecking(true);
    setError('');
    try {
      const result = await verifyWidget(siteId);
      if (result.verified) {
        setConnected(true);
        trackEvent('onboarding_connection_verified', { siteId });
      } else {
        setError(
          result.reason ??
            '接続を確認できませんでした。コードが正しく設置されているか確認してください。',
        );
      }
    } catch (err) {
      setError(formatErrorMessage(err, '確認に失敗しました'));
    } finally {
      setChecking(false);
    }
  };

  // Step 3: 記事作成へ
  const handleGoToEditor = () => {
    trackEvent('onboarding_completed', { siteId });
    navigate({
      to: '/sites/$siteId/posts/new',
      params: { siteId },
    });
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f8f7f6]">
      <div className="w-full max-w-[700px] rounded-[12px] border border-[#edebe8] bg-white px-12 py-10 shadow-[0_1px_3px_rgba(0,0,0,0.06)]">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-[13px] font-medium text-[#3b82f6]">ステップ {step} / 3</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#edebe8]">
            <div
              className="h-full bg-[#3b82f6] transition-all duration-300"
              style={{ width: `${(step / 3) * 100}%` }}
            />
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-[6px] border-l-4 border-[#ef4444] bg-[#fef2f2] p-3 text-sm text-[#991b1b]">
            {error}
          </div>
        )}

        {/* Step 1: サイト情報 */}
        {step === 1 && (
          <div>
            <h1 className="text-[20px] font-bold text-[#23221e]">最初のサイトを登録しましょう</h1>
            <p className="mt-2 text-[14px] text-[#706d65]">
              PetaCMS を設置するサイトの情報を入力してください
            </p>
            <form onSubmit={handleStep1} className="mt-6 space-y-4">
              <div>
                <label htmlFor="ob-name" className="block text-[13px] font-medium text-[#23221e]">
                  サイト名
                </label>
                <input
                  id="ob-name"
                  type="text"
                  required
                  value={siteName}
                  onChange={(e) => setSiteName(e.target.value)}
                  placeholder="例: MOVON コーポレートサイト"
                  className="mt-2 h-[44px] w-full rounded-[6px] border border-[#d6d3d0] px-3 text-sm text-[#23221e] placeholder:text-[#c1bdb7] focus:border-[#3b82f6] focus:outline-none focus:ring-2 focus:ring-[#3b82f6]"
                />
              </div>
              <div>
                <label htmlFor="ob-host" className="block text-[13px] font-medium text-[#23221e]">
                  サイト URL
                </label>
                <input
                  id="ob-host"
                  type="text"
                  required
                  value={originHost}
                  onChange={(e) => setOriginHost(e.target.value)}
                  placeholder="例: movon.jp"
                  className="mt-2 h-[44px] w-full rounded-[6px] border border-[#d6d3d0] px-3 text-sm text-[#23221e] placeholder:text-[#c1bdb7] focus:border-[#3b82f6] focus:outline-none focus:ring-2 focus:ring-[#3b82f6]"
                />
              </div>
              <button
                type="submit"
                disabled={createMutation.isPending}
                className="mt-6 h-[44px] w-full rounded-[6px] bg-[#3b82f6] text-sm font-medium text-white transition hover:bg-[#1d4ed8] disabled:opacity-50"
              >
                {createMutation.isPending ? '作成中...' : '次へ進む'}
              </button>
            </form>
            <button
              type="button"
              onClick={() => setStep(2)}
              className="mt-2 w-full text-center text-sm text-[#706d65] transition hover:text-[#3b82f6]"
            >
              あとで設定する
            </button>
          </div>
        )}

        {/* Step 2: 埋め込みコード */}
        {step === 2 && (
          <div>
            <h1 className="text-[20px] font-bold text-[#23221e]">コードを設置</h1>
            <p className="mt-2 text-[14px] text-[#706d65]">
              以下のコードをサイトの {'</body>'} の直前に貼り付けてください
            </p>
            <div className="mt-4 rounded-[8px] border border-[#edebe8] bg-[#f8f7f6] p-4">
              <pre className="overflow-x-auto text-xs text-[#23221e]">
                <code>{embedCode}</code>
              </pre>
              <button
                type="button"
                onClick={handleCopyEmbed}
                className="mt-2 rounded-[6px] border border-[#d6d3d0] px-3 py-1.5 text-xs font-medium text-[#706d65] hover:bg-white"
              >
                {copied ? '✓ コピー済み' : 'コピー'}
              </button>
            </div>

            {originHost && (
              <div className="mt-4 rounded-[6px] border border-[#edebe8] bg-[#f8f7f6] p-3 text-xs text-[#706d65]">
                設置後、
                <a
                  href={`https://${originHost}/`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mx-1 font-medium text-[#3b82f6] hover:underline"
                >
                  {originHost} を開いて ↗
                </a>
                確認できます
              </div>
            )}

            <div className="mt-6 space-y-3">
              {!connected ? (
                <button
                  type="button"
                  onClick={handleCheckConnection}
                  disabled={checking}
                  className="h-[44px] w-full rounded-[6px] border border-[#3b82f6] text-sm font-medium text-[#3b82f6] transition hover:bg-[#eff6ff] disabled:opacity-50"
                >
                  {checking ? '確認中...' : '接続を確認'}
                </button>
              ) : (
                <div className="rounded-[6px] border-l-4 border-[#10b981] bg-[#ecfdf5] p-3 text-center text-sm font-medium text-[#065f46]">
                  ✓ 接続成功！
                </div>
              )}

              {/* CSP トラブルシュート */}
              {error && (
                <details className="rounded-[6px] border-l-4 border-[#f59e0b] bg-[#fffbeb] p-3 text-xs text-[#92400e]">
                  <summary className="cursor-pointer font-medium">うまくいかない場合</summary>
                  <ul className="mt-2 list-inside list-disc space-y-1">
                    <li>
                      サイトに{' '}
                      <code className="rounded bg-[#fffbeb] px-1">Content-Security-Policy</code>{' '}
                      ヘッダが設定されている場合、
                      <code className="rounded bg-[#fffbeb] px-1">script-src</code> と{' '}
                      <code className="rounded bg-[#fffbeb] px-1">connect-src</code> に{' '}
                      <code className="rounded bg-[#fffbeb] px-1">https://cdn.peta-cms.com</code>{' '}
                      を追加してください
                    </li>
                    <li>
                      コードが <code className="rounded bg-[#fffbeb] px-1">{'</body>'}</code>{' '}
                      の直前に正しく配置されているか確認してください
                    </li>
                    <li>ブラウザのキャッシュをクリアして再度お試しください</li>
                  </ul>
                </details>
              )}

              <button
                type="button"
                onClick={() => setStep(3)}
                className="mt-6 h-[44px] w-full rounded-[6px] bg-[#3b82f6] text-sm font-medium text-white transition hover:bg-[#1d4ed8]"
              >
                {connected ? '次へ進む' : 'スキップして次へ'}
              </button>
            </div>
          </div>
        )}

        {/* Step 3: 最初の記事 */}
        {step === 3 && (
          <div className="text-center">
            <span className="text-5xl">🎉</span>
            <h1 className="mt-4 text-[20px] font-bold text-[#23221e]">準備完了！</h1>
            <p className="mt-2 text-[14px] text-[#706d65]">
              最初の記事を作成して、サイトに表示してみましょう
            </p>
            <button
              type="button"
              onClick={handleGoToEditor}
              className="mt-6 h-[44px] w-full rounded-[6px] bg-[#3b82f6] text-sm font-medium text-white transition hover:bg-[#1d4ed8]"
            >
              最初の記事を書く
            </button>
            <button
              type="button"
              onClick={() => navigate({ to: '/sites/$siteId/dashboard', params: { siteId } })}
              className="mt-3 h-[44px] w-full rounded-[6px] border border-[#d6d3d0] text-sm text-[#706d65] transition hover:bg-[#f8f7f6]"
            >
              ダッシュボードへ
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
